import { useEffect, useState } from "react";
import { Bell, Check, CheckCheck, Trophy, Ticket, Zap, Megaphone, Info, Share2, MessagesSquare, X, Loader2, Lock, Hash } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { PageHeader, FullPageLoader, EmptyState, InlineLoader } from "@/components/common";

const typeIcon = {
  booking: { icon: Ticket, color: "#00e5ff" },
  tournament: { icon: Trophy, color: "#ffb020" },
  points: { icon: Zap, color: "#00ff9d" },
  promotion: { icon: Megaphone, color: "#ff2e97" },
  system: { icon: Info, color: "#8b5cf6" },
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [shareFor, setShareFor] = useState(null);

  const load = () => {
    apiClient.get("/notifications", { params: { limit: 50 } })
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    try { await apiClient.put(`/notifications/${id}/read`); } catch (e) { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await apiClient.put("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  if (loading) return <FullPageLoader label="Loading Notifications" />;

  const hasUnread = items.some((n) => !n.is_read);
  const unreadCount = items.filter((n) => !n.is_read).length;
  const visible = filter === "unread" ? items.filter((n) => !n.is_read) : items;

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle="Stay updated on bookings, tournaments & rewards."
        right={hasUnread && (
          <button onClick={markAll} className="neon-outline-btn px-5 py-2.5" data-testid="mark-all-read">
            <CheckCheck className="w-4 h-4" /> Mark All Read
          </button>
        )}
      />

      <div className="flex gap-2 mb-5">
        {["all", "unread"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className="chip" data-testid={`notif-filter-${f}`}
            style={{
              background: filter === f ? "var(--neon-cyan)" : "rgba(8,11,22,0.6)",
              border: `1px solid ${filter === f ? "var(--neon-cyan)" : "rgba(120,150,220,0.18)"}`,
              color: filter === f ? "#05060d" : "#cbd5e1",
              fontWeight: 700,
            }}
          >
            {f.toUpperCase()}{f === "unread" && unreadCount > 0 ? ` · ${unreadCount}` : ""}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={Bell} title={filter === "unread" ? "All caught up!" : "No notifications"} subtitle={filter === "unread" ? "You've read everything." : "You're all caught up. New alerts will appear here."} />
      ) : (
        <div className="space-y-3">
          {visible.map((n) => {
            const meta = typeIcon[n.type] || typeIcon.system;
            return (
              <div key={n.id}
                className="glass rounded-2xl p-5 flex items-start gap-4 transition-colors"
                style={!n.is_read ? { border: "1px solid var(--neon-cyan)", background: "color-mix(in srgb, var(--neon-cyan) 8%, transparent)" } : {}}
                data-testid={`notification-${n.id}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${meta.color}1f`, border: `1px solid ${meta.color}55` }}>
                  <meta.icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold">{n.title}</p>
                    {!n.is_read && <span className="w-2 h-2 rounded-full pulse-dot" style={{ background: "var(--neon-cyan)" }} />}
                  </div>
                  <p className="text-slate-400 text-sm mt-0.5">{n.message}</p>
                  <p className="text-slate-600 text-xs mt-2">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</p>
                  <button onClick={() => setShareFor(n)} data-testid={`share-notif-${n.id}`}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold transition-colors text-slate-400 hover:text-cyan-400">
                    <Share2 className="w-3.5 h-3.5" /> Share to chat
                  </button>
                </div>
                {!n.is_read && (
                  <button onClick={() => markRead(n.id)} className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid={`read-${n.id}`}>
                    <Check className="w-5 h-5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {shareFor && <ShareToChatModal notification={shareFor} onClose={() => setShareFor(null)} />}
    </div>
  );
}

function buildShareText(n) {
  if (n?.data?.share_text) return n.data.share_text;
  let msg = n?.message || "";
  // Strip any "— you've earned N pts" / "N bonus pts" clause so points aren't shared.
  msg = msg
    .replace(/\s*[—-]\s*(you'?ve|you have)\s+earned\s+[\d,]+\s*(bonus\s+)?pts?\.?/i, "")
    .replace(/\s*[—-]\s*[\d,]+\s*(bonus\s+)?pts?\.?/i, "")
    .trim();
  return `🏁 ${n?.title || ""}${msg ? ` — ${msg}` : ""}`.trim();
}

function ShareToChatModal({ notification, onClose }) {
  const [rooms, setRooms] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    apiClient.get("/chat/rooms")
      .then(({ data }) => setRooms(data))
      .catch(() => toast.error("Could not load channels"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const message = buildShareText(notification);

  const post = async () => {
    if (selected.size === 0) { toast.error("Pick at least one channel"); return; }
    setPosting(true);
    try {
      await Promise.all([...selected].map((id) => apiClient.post(`/chat/rooms/${id}/messages`, { message })));
      toast.success(`Shared to ${selected.size} channel${selected.size > 1 ? "s" : ""}`);
      onClose();
    } catch (err) { toast.error(getErrorMessage(err, "Share failed")); } finally { setPosting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="share-to-chat-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-white flex items-center gap-2"><MessagesSquare className="w-5 h-5" style={{ color: "#00e5ff" }} /> Share to Chat</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="share-close"><X className="w-6 h-6" /></button>
        </div>
        <div className="rounded-xl p-3 mb-4 text-sm text-slate-300" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
          {message}
        </div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Select channels</p>
        {loading ? <InlineLoader label="Loading channels" /> : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto scrollbar-thin mb-4">
            {rooms.map((r) => {
              const on = selected.has(r.id);
              return (
                <button key={r.id} onClick={() => toggle(r.id)} data-testid={`share-room-${r.id}`}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl font-semibold text-left transition-all"
                  style={on
                    ? { background: "rgba(0,229,255,0.14)", border: "1px solid rgba(0,229,255,0.5)", color: "#fff" }
                    : { border: "1px solid rgba(120,150,220,0.14)", color: "#94a3b8" }}>
                  {r.is_public === false ? <Lock className="w-4 h-4 shrink-0" style={{ color: "#ff2e97" }} /> : <Hash className="w-4 h-4 shrink-0" />}
                  <span className="truncate flex-1">{r.name}</span>
                  {on && <Check className="w-4 h-4" style={{ color: "#00e5ff" }} />}
                </button>
              );
            })}
          </div>
        )}
        <button onClick={post} disabled={posting || selected.size === 0} className="neon-btn w-full py-3" data-testid="share-submit">
          {posting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sharing...</> : <><Share2 className="w-4 h-4" /> Share{selected.size > 0 ? ` (${selected.size})` : ""}</>}
        </button>
      </div>
    </div>
  );
}
